import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { Loader2, User, AlertCircle, Save, CreditCard, Calendar } from 'lucide-react';
import { PhoneInput } from '../ui/phone-input';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { clientsApi, comptesApi } from '../../api';
import { agentsApi } from '../../api/agents.api';
import type { Client, Compte } from '../../api/clients.api';
import { toast } from '../../hooks/use-toast';

interface EditClientModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    client: Client | null;
    compte: Compte | null;
}

interface EditData {
    prenom: string;
    nom: string;
    surnom: string;
    telephone: string;
    email: string;
    sexe: string;
    adresse: string;
    ville: string;
    pays: string;
    dateNaissance: string;
}

interface FieldErrors {
    prenom?: string;
    nom?: string;
    telephone?: string;
    email?: string;
    dateDebut?: string;
    duree?: string;
    nbMain?: string;
    sol?: string;
    general?: string;
}

// Dat jounen an nan timezone lokal (evite bug UTC)
const getLocalToday = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Kalkile dat fin SOL selon lojik backend: jours sote dimanch
const calculerDateFin = (dateDebut: string, duree: number, frequence: string): string => {
    if (!dateDebut || !duree || duree <= 0) return '';
    const current = new Date(dateDebut + 'T12:00:00');
    if (isNaN(current.getTime())) return '';

    if (frequence === 'jours') {
        let count = 0;
        while (count < duree) {
            if (current.getDay() !== 0) count++;
            if (count < duree) current.setDate(current.getDate() + 1);
        }
    } else if (frequence === 'semaine') {
        current.setDate(current.getDate() + (duree - 1) * 7);
    } else if (frequence === 'mois') {
        current.setMonth(current.getMonth() + (duree - 1));
    }

    return `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`;
};

export function EditClientModal({ isOpen, onClose, onSuccess, client, compte }: EditClientModalProps) {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [activeTab, setActiveTab] = useState<'client' | 'account'>('client');
    const [agents, setAgents] = useState<any[]>([]);

    const [formData, setFormData] = useState<EditData>({
        prenom: '',
        nom: '',
        surnom: '',
        telephone: '',
        email: '',
        sexe: '',
        adresse: '',
        ville: '',
        pays: '',
        dateNaissance: '',
    });

    const [accountData, setAccountData] = useState({
        type: 'BS',
        agentId: 'none',
        planSol: 'none',
        dateDebut: getLocalToday(),
        frequence: 'jours',
        duree: 30,
        nbMain: 1,
        sol: 100,
    });

    useEffect(() => {
        if (isOpen) {
            agentsApi.getAll()
                .then(res => {
                    if (res && res.agents) {
                        setAgents(res.agents);
                    }
                })
                .catch(err => console.error('Error fetching agents:', err));
        }
    }, [isOpen]);

    useEffect(() => {
        if (client && isOpen) {
            setFormData({
                prenom: client.prenom || '',
                nom: client.nom || '',
                surnom: client.surnom || '',
                telephone: client.telephone || '',
                email: client.email || '',
                sexe: client.sexe || '',
                adresse: client.adresse || '',
                ville: client.ville || '',
                pays: client.pays || '',
                dateNaissance: client.dateNaissance ? new Date(client.dateNaissance).toISOString().split('T')[0] : '',
            });

            if (compte) {
                setAccountData({
                    type: compte.type || 'BS',
                    agentId: compte.agentId || 'none',
                    planSol: compte.planSol || 'none',
                    dateDebut: compte.dateDebut ? new Date(compte.dateDebut).toISOString().split('T')[0] : getLocalToday(),
                    frequence: (compte.frequence as any) || 'jours',
                    duree: compte.duree || 30,
                    nbMain: compte.nbMain || 1,
                    sol: compte.sol || 100,
                });
            } else {
                setAccountData({
                    type: 'BS',
                    agentId: 'none',
                    planSol: 'none',
                    dateDebut: getLocalToday(),
                    frequence: 'jours',
                    duree: 30,
                    nbMain: 1,
                    sol: 100,
                });
            }
            setActiveTab('client');
            setFieldErrors({});
        }
    }, [client, compte, isOpen]);

    const handleClientChange = (field: keyof EditData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (fieldErrors[field as keyof FieldErrors] || fieldErrors.general) {
            setFieldErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field as keyof FieldErrors];
                delete newErrors.general;
                return newErrors;
            });
        }
    };

    const handleAccountChange = (field: string, value: any) => {
        setAccountData(prev => ({ ...prev, [field]: value }));
        if (fieldErrors[field as keyof FieldErrors] || fieldErrors.general) {
            setFieldErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field as keyof FieldErrors];
                delete newErrors.general;
                return newErrors;
            });
        }
    };

    const validate = (): boolean => {
        const errors: FieldErrors = {};

        // Client validation
        if (!formData.prenom.trim()) errors.prenom = t('common:validation.required');
        if (!formData.nom.trim()) errors.nom = t('common:validation.required');
        
        if (!formData.telephone.trim()) {
            errors.telephone = t('common:validation.required');
        } else if (!isValidPhoneNumber(formData.telephone)) {
            errors.telephone = t('common:validation.invalidPhone');
        }

        // Account validation if compte exists and type is SOL
        if (compte && accountData.type === 'SOL') {
            if (!accountData.dateDebut) errors.dateDebut = t('common:validation.required');
            if (!accountData.duree || accountData.duree <= 0) errors.duree = t('common:validation.required');
            if (!accountData.nbMain || accountData.nbMain <= 0) errors.nbMain = t('common:validation.required');
            if (!accountData.sol || accountData.sol <= 0) errors.sol = t('common:validation.required');
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            // Switch tabs dynamically if error is in another tab
            const hasClientErrors = errors.prenom || errors.nom || errors.telephone || errors.email;
            const hasAccountErrors = errors.dateDebut || errors.duree || errors.nbMain || errors.sol;
            if (hasClientErrors && activeTab !== 'client') {
                setActiveTab('client');
            } else if (hasAccountErrors && activeTab !== 'account') {
                setActiveTab('account');
            }
            return false;
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validate() || !client) return;

        setIsLoading(true);
        setFieldErrors({});

        try {
            // 1. Update Client info
            const updatePayload: any = {
                prenom: formData.prenom.trim(),
                nom: formData.nom.trim(),
                surnom: formData.surnom.trim() || null,
                telephone: formData.telephone,
                email: formData.email.trim() || null,
                sexe: formData.sexe || null,
                adresse: formData.adresse.trim() || null,
                ville: formData.ville.trim() || null,
                pays: formData.pays.trim() || null,
                dateNaissance: formData.dateNaissance || null,
            };

            const clientResponse = await clientsApi.update(client.id, updatePayload);
            if (!clientResponse.success) {
                throw new Error('Mete ajou kliyan an echwe.');
            }

            // 2. Update Account parameters (if compte exists)
            if (compte) {
                const accountPayload: any = {
                    type: accountData.type,
                    agentId: accountData.agentId === 'none' ? null : accountData.agentId,
                };

                if (accountData.type === 'SOL') {
                    accountPayload.dateDebut = accountData.dateDebut;
                    accountPayload.frequence = accountData.frequence;
                    accountPayload.duree = Number(accountData.duree);
                    accountPayload.nbMain = Number(accountData.nbMain);
                    accountPayload.sol = Number(accountData.sol);
                } else if (accountData.type === 'BS') {
                    accountPayload.planSol = accountData.planSol === 'none' ? null : accountData.planSol;
                }

                const compteResponse = await comptesApi.update(compte.id, accountPayload);
                if (!compteResponse.success) {
                    throw new Error('Mete ajou kont lan echwe.');
                }
            }

            toast({
                title: t('common:success'),
                description: t('accounts:clients.dialogs.edit.success') || 'Enfòmasyon kliyan an aktyalize ak siksè.',
                type: 'success',
            });
            onSuccess();
            onClose();
        } catch (err: any) {
            const message = err.response?.data?.message || err.message || t('common:error');
            setFieldErrors({ general: message });
            console.error('Update client/compte error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const hasCredentialChanges = client && (
        formData.telephone !== client.telephone || 
        formData.email !== client.email
    );

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="w-[95vw] max-w-[550px] max-h-[90vh] overflow-y-auto p-0 rounded-xl border-none shadow-2xl bg-slate-50">
                <DialogHeader className="p-4 sm:p-6 bg-white border-b border-slate-100 rounded-t-xl">
                    <DialogTitle className="flex items-center gap-3 text-lg font-bold text-slate-900">
                        <div className="bg-primary-50 p-2.5 rounded-xl text-primary-600 shadow-sm shrink-0">
                            <User className="h-5 w-5" />
                        </div>
                        {compte ? 'Modifikasyon Kliyan & Kont' : t('accounts:clients.actions.edit') || 'Modifye Kliyan'}
                    </DialogTitle>
                    <DialogDescription className="text-xs sm:text-sm">
                        {compte 
                            ? 'Modifye enfòmasyon pèsonèl kliyan an oswa ajiste paramèt ak tip kont li.' 
                            : t('accounts:clients.dialogs.edit.description') || 'Modifye enfòmasyon pèsonèl kliyan an.'}
                    </DialogDescription>
                </DialogHeader>

                {/* Switch Tabs if compte exists */}
                {compte && (
                    <div className="flex border-b border-slate-200 bg-white px-4 sm:px-6">
                        <button
                            type="button"
                            className={`flex-1 py-3 text-center text-xs sm:text-sm font-bold border-b-2 transition-all flex items-center justify-center gap-2 ${
                                activeTab === 'client'
                                    ? 'border-primary-600 text-primary-600'
                                    : 'border-transparent text-slate-500 hover:text-slate-700'
                            }`}
                            onClick={() => setActiveTab('client')}
                        >
                            <User className="h-4 w-4" />
                            Enfòmasyon Pèsonèl
                        </button>
                        <button
                            type="button"
                            className={`flex-1 py-3 text-center text-xs sm:text-sm font-bold border-b-2 transition-all flex items-center justify-center gap-2 ${
                                activeTab === 'account'
                                    ? 'border-primary-600 text-primary-600'
                                    : 'border-transparent text-slate-500 hover:text-slate-700'
                            }`}
                            onClick={() => setActiveTab('account')}
                        >
                            <CreditCard className="h-4 w-4" />
                            Paramèt Kont
                        </button>
                    </div>
                )}

                <div className="p-4 sm:p-6 space-y-4">
                    {fieldErrors.general && (
                        <div className="flex items-center gap-3 p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl">
                            <AlertCircle className="h-5 w-5 flex-shrink-0" />
                            <span className="font-medium">{fieldErrors.general}</span>
                        </div>
                    )}

                    {/* Type migration warning */}
                    {compte && activeTab === 'account' && accountData.type !== compte.type && (
                        <div className="p-3.5 text-xs text-red-800 bg-red-50 border border-red-200 rounded-xl flex flex-col gap-1.5 shadow-sm animate-pulse">
                            <div className="flex items-center gap-2 font-bold text-red-900 uppercase tracking-wide">
                                <AlertCircle className="h-4.5 w-4.5 text-red-700 animate-bounce" />
                                <span>Atansyon: Migrasyon Kont</span>
                            </div>
                            <p>
                                W ap chanje tip kont lan de <strong>{compte.type}</strong> pou <strong>{accountData.type}</strong>. 
                                Lè ou anrejistre:
                            </p>
                            <ul className="list-disc pl-4 space-y-1 font-semibold text-slate-800">
                                <li>Nimewo kont la (code) ap chanje ak yon nouvo prefiks otomatik.</li>
                                <li>Balans kont aktyèl la ({compte.type === 'BS' ? (compte.soldeBS ?? 0) : (compte.soldeS ?? 0)} HTG) ap transfere otomatikman sou nouvo kont lan.</li>
                            </ul>
                        </div>
                    )}

                    {/* Tab 1: Client Info */}
                    {activeTab === 'client' && (
                        <div className="space-y-4 bg-white p-4 rounded-xl shadow-sm border border-slate-100">
                            {hasCredentialChanges && (
                                <div className="p-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg flex flex-col gap-1">
                                    <div className="flex items-center gap-1.5 font-bold">
                                        <AlertCircle className="h-4 w-4" />
                                        <span>{t('accounts:clients.dialogs.edit.credentialWarningTitle') || 'Atansyon'}</span>
                                    </div>
                                    <p>{t('accounts:clients.dialogs.edit.credentialWarningDescription') || 'Chanjman nan telefòn oswa email ap aktyalize tou idantifyan koneksyon kliyan an.'}</p>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-3 sm:gap-4">
                                <div className="space-y-1.5">
                                    <Label htmlFor="edit-prenom">{t('common:labels.firstName')} <span className="text-red-500">*</span></Label>
                                    <Input
                                        id="edit-prenom"
                                        value={formData.prenom}
                                        onChange={(e) => handleClientChange('prenom', e.target.value)}
                                        className={fieldErrors.prenom ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'bg-slate-50/50 border-slate-200'}
                                    />
                                    {fieldErrors.prenom && <p className="text-xs text-red-500">{fieldErrors.prenom}</p>}
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="edit-nom">{t('common:labels.lastName')} <span className="text-red-500">*</span></Label>
                                    <Input
                                        id="edit-nom"
                                        value={formData.nom}
                                        onChange={(e) => handleClientChange('nom', e.target.value)}
                                        className={fieldErrors.nom ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'bg-slate-50/50 border-slate-200'}
                                    />
                                    {fieldErrors.nom && <p className="text-xs text-red-500">{fieldErrors.nom}</p>}
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                <Label htmlFor="edit-surnom">{t('common:labels.nickname')}</Label>
                                <Input
                                    id="edit-surnom"
                                    value={formData.surnom}
                                    onChange={(e) => handleClientChange('surnom', e.target.value)}
                                    className="bg-slate-50/50 border-slate-200"
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                <div className="space-y-1.5">
                                    <Label htmlFor="edit-telephone">{t('common:labels.phone')} <span className="text-red-500">*</span></Label>
                                    <PhoneInput
                                        id="edit-telephone"
                                        value={formData.telephone}
                                        onChange={(val: string | undefined) => handleClientChange('telephone', val || '')}
                                        defaultCountry="HT"
                                        className={fieldErrors.telephone ? 'border-red-500' : 'bg-slate-50/50 border-slate-200'}
                                    />
                                    {fieldErrors.telephone && <p className="text-xs text-red-500">{fieldErrors.telephone}</p>}
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="edit-email">{t('common:labels.email')}</Label>
                                    <Input
                                        id="edit-email"
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => handleClientChange('email', e.target.value)}
                                        className="bg-slate-50/50 border-slate-200"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3 sm:gap-4">
                                <div className="space-y-1.5">
                                    <Label htmlFor="edit-sexe">{t('common:labels.gender')}</Label>
                                    <Select value={formData.sexe} onValueChange={(val) => handleClientChange('sexe', val)}>
                                        <SelectTrigger id="edit-sexe" className="bg-slate-50/50 border-slate-200">
                                            <SelectValue placeholder={t('common:labels.choose')} />
                                        </SelectTrigger>
                                        <SelectContent side="top">
                                            <SelectItem value="M">{t('common:labels.male')}</SelectItem>
                                            <SelectItem value="F">{t('common:labels.female')}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="edit-dateNaissance">{t('common:labels.birthDate')}</Label>
                                    <Input
                                        id="edit-dateNaissance"
                                        type="date"
                                        value={formData.dateNaissance}
                                        onChange={(e) => handleClientChange('dateNaissance', e.target.value)}
                                        className="bg-slate-50/50 border-slate-200"
                                    />
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                <Label htmlFor="edit-adresse">{t('common:labels.address')}</Label>
                                <Input
                                    id="edit-adresse"
                                    value={formData.adresse}
                                    onChange={(e) => handleClientChange('adresse', e.target.value)}
                                    className="bg-slate-50/50 border-slate-200"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3 sm:gap-4">
                                <div className="space-y-1.5">
                                    <Label htmlFor="edit-ville">{t('common:labels.city')}</Label>
                                    <Input
                                        id="edit-ville"
                                        value={formData.ville}
                                        onChange={(e) => handleClientChange('ville', e.target.value)}
                                        className="bg-slate-50/50 border-slate-200"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="edit-pays">{t('common:labels.country') || 'Pays'}</Label>
                                    <Input
                                        id="edit-pays"
                                        value={formData.pays}
                                        onChange={(e) => handleClientChange('pays', e.target.value)}
                                        className="bg-slate-50/50 border-slate-200"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Tab 2: Account Params */}
                    {compte && activeTab === 'account' && (
                        <div className="space-y-4 bg-white p-4 rounded-xl shadow-sm border border-slate-100">
                            {/* Tip Kont & Ajan asiye */}
                            <div className="grid grid-cols-2 gap-3 sm:gap-4">
                                <div className="space-y-1.5">
                                    <Label className="text-sm font-semibold text-slate-700">Tip Kont</Label>
                                    <Select value={accountData.type} onValueChange={(val) => handleAccountChange('type', val)}>
                                        <SelectTrigger className="bg-slate-50/50 border-slate-200">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="BS">BS (Bousanm Standard)</SelectItem>
                                            <SelectItem value="SOL">SOL (Sabotay)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-sm font-semibold text-slate-700">Ajan Asiye</Label>
                                    <Select value={accountData.agentId} onValueChange={(val) => handleAccountChange('agentId', val)}>
                                        <SelectTrigger className="bg-slate-50/50 border-slate-200">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="none">Okenn / Libere</SelectItem>
                                            {agents.map((a) => (
                                                <SelectItem key={a.id} value={a.id}>
                                                    {a.prenom} {a.nom} ({a.code})
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* BS Specific Fields */}
                            {accountData.type === 'BS' && (
                                <div className="space-y-1.5 pt-2">
                                    <Label className="text-sm font-semibold text-slate-700">Plan Solidaire</Label>
                                    <Select value={accountData.planSol} onValueChange={(val) => handleAccountChange('planSol', val)}>
                                        <SelectTrigger className="bg-slate-50/50 border-slate-200">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent side="top">
                                            <SelectItem value="none">Okenn / N/A</SelectItem>
                                            <SelectItem value="espwa">Espwa (100 HTG/semèn)</SelectItem>
                                            <SelectItem value="fos">Fòs (200 HTG/semèn)</SelectItem>
                                            <SelectItem value="majeste">Majeste (400 HTG/semèn)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}

                            {/* SOL Specific Fields */}
                            {accountData.type === 'SOL' && (
                                <div className="space-y-4 pt-2">
                                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                                        <div className="space-y-1.5">
                                            <Label className="text-sm font-semibold text-slate-700">Dat Kòmansman <span className="text-red-500">*</span></Label>
                                            <Input
                                                type="date"
                                                value={accountData.dateDebut}
                                                onChange={(e) => handleAccountChange('dateDebut', e.target.value)}
                                                className={fieldErrors.dateDebut ? 'border-red-500' : 'bg-slate-50/50 border-slate-200'}
                                            />
                                            {fieldErrors.dateDebut && <p className="text-xs text-red-500">{fieldErrors.dateDebut}</p>}
                                        </div>
                                        <div className="space-y-1.5">
                                            <Label className="text-sm font-semibold text-slate-700">Echeyans</Label>
                                            <Select value={accountData.frequence} onValueChange={(val) => handleAccountChange('frequence', val)}>
                                                <SelectTrigger className="bg-slate-50/50 border-slate-200">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="jours">Jounalye (jours)</SelectItem>
                                                    <SelectItem value="semaine">Semenn (semaine)</SelectItem>
                                                    <SelectItem value="mois">Mansyèl (mois)</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-3 gap-3">
                                        <div className="space-y-1.5">
                                            <Label className="text-xs font-semibold text-slate-700">Diri (Cycles) <span className="text-red-500">*</span></Label>
                                            <Input
                                                type="number"
                                                min="1"
                                                value={accountData.duree}
                                                onChange={(e) => handleAccountChange('duree', parseInt(e.target.value) || 0)}
                                                className={fieldErrors.duree ? 'border-red-500' : 'bg-slate-50/50 border-slate-200'}
                                            />
                                            {fieldErrors.duree && <p className="text-xs text-red-500">{fieldErrors.duree}</p>}
                                        </div>
                                        <div className="space-y-1.5">
                                            <Label className="text-xs font-semibold text-slate-700">Men (Hands) <span className="text-red-500">*</span></Label>
                                            <Input
                                                type="number"
                                                min="1"
                                                value={accountData.nbMain}
                                                onChange={(e) => handleAccountChange('nbMain', parseInt(e.target.value) || 0)}
                                                className={fieldErrors.nbMain ? 'border-red-500' : 'bg-slate-50/50 border-slate-200'}
                                            />
                                            {fieldErrors.nbMain && <p className="text-xs text-red-500">{fieldErrors.nbMain}</p>}
                                        </div>
                                        <div className="space-y-1.5">
                                            <Label className="text-xs font-semibold text-slate-700">Miz (Sol) <span className="text-red-500">*</span></Label>
                                            <Input
                                                type="number"
                                                min="1"
                                                value={accountData.sol}
                                                onChange={(e) => handleAccountChange('sol', parseInt(e.target.value) || 0)}
                                                className={fieldErrors.sol ? 'border-red-500' : 'bg-slate-50/50 border-slate-200'}
                                            />
                                            {fieldErrors.sol && <p className="text-xs text-red-500">{fieldErrors.sol}</p>}
                                        </div>
                                    </div>

                                    {/* Calculated info */}
                                    <div className="p-3.5 bg-primary-50/50 border border-primary-100 rounded-xl space-y-1.5 text-xs text-primary-900">
                                        <div className="flex justify-between items-center">
                                            <span className="font-semibold text-slate-500 uppercase">Miz total pa echeyans:</span>
                                            <span className="font-black text-sm text-primary-700">
                                                {accountData.nbMain * accountData.sol} HTG
                                            </span>
                                        </div>
                                        {(() => {
                                            const dateFin = calculerDateFin(accountData.dateDebut, accountData.duree, accountData.frequence);
                                            if (!dateFin) return null;
                                            const d = new Date(dateFin + 'T12:00:00');
                                            const label = d.toLocaleDateString('fr-HT', { day: '2-digit', month: 'long', year: 'numeric' });
                                            return (
                                                <div className="flex justify-between items-center pt-1 border-t border-primary-100/50">
                                                    <span className="font-semibold text-slate-500 uppercase">Dat fin Sabotay:</span>
                                                    <span className="font-bold text-primary-600 flex items-center gap-1">
                                                        <Calendar className="h-3.5 w-3.5" />
                                                        {label}
                                                    </span>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <DialogFooter className="p-4 sm:p-6 bg-white border-t border-slate-100 rounded-b-xl gap-2 sm:gap-0">
                    <Button variant="outline" onClick={onClose} disabled={isLoading} className="border-slate-200">
                        {t('common:buttons.cancel')}
                    </Button>
                    <Button onClick={handleSubmit} disabled={isLoading} className="gap-2 font-bold shadow-md shadow-primary-100">
                        {isLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Save className="h-4 w-4" />
                        )}
                        {t('common:buttons.save') || 'Anrejistre'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default EditClientModal;
